import React, { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Alert, Chip, Divider, IconButton, Stack, Typography } from '@mui/material'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import { useNavigate, useParams } from 'react-router-dom'
import WebService from '@util/webService'

/**
 * 설정 화면 -- **읽기 전용이다.**
 *
 * 트레이더의 손잡이가 DB 어디에 어떤 값으로 적혀 있는지를 한 장으로 보는 자리다. 고치는
 * 창구는 내지 않는다(주인 지시 2026-08-28) -- 이 값들은 **조립 시점에 고정**되므로 돌고 있는
 * 프로세스에 대고 바꿔 봐야 다음 재기동까지 아무 일도 안 일어난다. 화면에 저장 버튼이 있으면
 * 그 사실이 가려진다.
 *
 * == ⚠ 출처가 바뀌었다 (2026-08-28) ==
 *
 * 옛 판은 `user_symbol_configs`(EAV, `(user_id, symbol_id)` 축)를 그렸다. 그 표가 정하던 셋
 * (`volumeCapRatio` · `maxNotional` · `savingRatio`)이 **`wallets` 칸으로 옮겨가면서**
 * 트레이더가 그 표를 더는 안 읽는다(주인: "당분간 사용하지 않음"). 표와 `UscController` 는
 * 그대로 두되 이 화면은 **지갑 행**을 그린다.
 *
 * 옮긴 값이 원래 지갑 축이었다는 것은 `savingRatio` 하나만 봐도 드러난다 -- 이체는 지갑과
 * 마스터 계좌 사이의 돈 이동이라 심볼이 낄 자리가 없었고, 옛 표는 `run_mode` 도 몰라서 같은
 * 심볼의 실거래 지갑과 테스트넷 지갑이 값 하나를 나눠 쓰고 있었다.
 *
 * == 정본은 트레이더다 ==
 *
 * 각 칸의 뜻은 트레이더가 정본이고 여기 있는 건 그걸 사람 말로 옮긴 **설명**이다.
 * **값 검증은 하지 않는다** -- 범위 규칙까지 베끼면 판정이 두 곳에 살고, 한쪽만 고쳐졌을 때
 * 화면이 "정상"이라 말하는데 트레이더는 그룹을 안 띄우는 상태가 된다. 그 어긋남은 조용하다.
 *
 * 다만 **0 의 뜻**만은 칸마다 적어 둔다. 같은 0 이 어디서는 "끔"이고 어디서는 "기동 실패"라,
 * 그 차이를 화면이 말하지 않으면 사람이 0 을 다 같은 것으로 읽는다.
 */

/**
 * 트레이더가 읽는 지갑 칸.
 *
 * `key` 는 서버가 주는 `WalletsRow` 필드명(카멜)이고 `label` 은 DB 컬럼명이다 -- **둘 다
 * 적는다.** 사람이 DB 를 열어 고칠 때 찾는 이름은 컬럼명이고, 응답을 볼 때 보이는 이름은
 * 필드명이라, 하나만 적으면 다른 쪽을 찾을 때 이 화면이 도움이 안 된다.
 *
 * `zero` 는 **0 이 무슨 뜻인가**다.
 *   'off'      0 이 끔이다 -- 트레이더의 어휘 그대로(`volumeCapRatio <= 0.0` 이 캡 없음)
 *   'reject'   0 이면 그 그룹이 기동에서 빠진다 -- 크기 0 이나 문턱 0 은 뜻이 없다
 *   null       0 이 특별한 뜻을 갖지 않는다
 */
const FIELDS = [
  {
    key: 'slotMultiple', label: 'slot_multiple', unit: null, zero: 'reject',
    desc: '확정본의 배수(L). 크기 식의 한 항이다 -- 확정본 2.08',
  },
  {
    key: 'slotFactor', label: 'slot_factor', unit: 'text', zero: null,
    desc: '눈금별 자리 무게. `4h:1, 1h:1` 모양이고 안 적은 눈금은 1.0. 빈 값이면 전 눈금 1.0',
  },
  {
    key: 'maxTradeRiskRatio', label: 'max_trade_risk_ratio', unit: 'ratio', zero: null,
    desc: '거래당 리스크 r. GZ 가 켜져 있으면 이 값이 그 위 천장(rMax)이다',
  },
  {
    key: 'ddMaxRatio', label: 'dd_max_ratio', unit: 'ratio', zero: 'off',
    desc: 'GZ 의 dMax. 0 이면 GZ 를 끈다 -- 끄는 법이 이 칸 하나다',
  },
  {
    key: 'ddRiskFloorRatio', label: 'dd_risk_floor_ratio', unit: 'ratio', zero: null,
    desc: 'GZ 의 바닥 rFloor. 낙폭이 깊어져도 r 이 이 아래로는 안 내려간다',
  },
  {
    /** 이름이 둘이던 시절이 있었다(`noAddLeverage` · `maxEntryLeverage`). 한 칸이 두 층에
     * 걸린다는 사실이 그 혼동의 원인이었고, 2026-08-27 에 이름을 하나로 합쳤다. */
    key: 'defendLeverage', label: 'defend_leverage', unit: 'x', zero: 'reject',
    desc: '문턱 -- 계좌 진입금지선이자 자리의 진입 레버리지 캡. 두 층이 이 한 칸을 같이 읽는다',
  },
  {
    key: 'maxLeverage', label: 'max_leverage', unit: 'x', zero: 'reject',
    desc: '청산 -- 계좌 전량청산선이자 자리 이탈선 공식의 배수. 거래소에 거는 레버리지도 이 값이다',
  },
  {
    key: 'volumeCapRatio', label: 'volume_cap_ratio', unit: 'ratio', zero: 'off',
    desc: '그 순간 거래대금 대비 주문 상한. 0 이면 끔',
  },
  {
    key: 'maxNotional', label: 'max_notional', unit: null, zero: 'off',
    desc: '주문 명목가 절대 상한. 0 이면 끔',
  },
  {
    /** 양방향이지만 **부호가 아니라 그 거래의 손익이 방향을 정한다** -- 이익이면 이 비율만큼
     * 보내고, 손실이면 이 비율만큼(마스터 잔고 한도 내에서) 받아온다. 그래서 값은 항상
     * 양수이고, 음수를 넣으면 트레이더가 거부한다. */
    key: 'savingRatio', label: 'saving_ratio', unit: 'ratio', zero: 'off',
    desc: '청산 후 마스터 이체 비율. 이익이면 보내고 손실이면 받아온다. 0 이면 끔',
  },
]

/** 비율을 % 로 같이 보여준다. **값을 바꾸지 않는다** -- 원문을 그대로 두고 괄호로 덧붙인다.
 * `Ratio` 규약이 소수(0.02 = 2%)라, 소수만 보이면 2%인지 200%인지 눈으로 안 갈린다. */
const asPercent = (raw) => {
  const v = Number(raw)
  if (!Number.isFinite(v)) {
    return null
  }
  return `${(v * 100).toLocaleString('ko-KR', { maximumFractionDigits: 4 })}%`
}

/**
 * `OptionSerializer`가 `None`을 `null`이 아니라 **빈 문자열**로 보낸다(util/wallet.js의
 * `optNum` 주석과 같은 자리). `??`로 못 거르므로 여기서 함께 처리한다.
 */
const optStr = (v) => {
  if (v === null || v === undefined || v === '') {
    return null
  }
  return String(v)
}

const UscPage = () => {
  const navigate = useNavigate()
  const { userId } = useParams()
  const account = useSelector((state) => state.accountReducer)

  /** `useWallets`와 **같은 유도**다 -- 경로에 userId가 있으면 그 유저, 없으면 본인.
   * 서버(`WalletController.listByUserId`)가 같은 규칙으로 다시 보므로 여기 값은 편의지
   * 권한이 아니다. */
  const targetUserId = (userId === undefined || userId === null || userId === '')
    ? account?.user?.id
    : userId

  const [rows, set_rows] = useState([])
  const [loading, set_loading] = useState(true)
  const [error, set_error] = useState('')

  const fetchRows = useCallback(async () => {
    if (targetUserId === undefined || targetUserId === null) {
      return
    }
    /** 지갑 화면과 **같은 창구**다(`useWallets`). 설정 전용 엔드포인트를 새로 내지 않는
     * 이유: 같은 행을 두 경로로 내주면 한쪽만 필드가 늘었을 때 두 화면이 다른 말을 한다. */
    const resp = await WebService.get(`/wallet/user/${targetUserId}`)
    if (resp.repCode !== 'ack') {
      console.error('wallet fetch failed=>', resp.repMessage)
      // **rows는 그대로 둔다.** 조회 실패는 상태가 아니라 사건이다 -- 비우면 "지갑이 없다"와
      // 구별되지 않는다(지갑 화면과 같은 규칙).
      set_error(resp.repMessage || '지갑을 불러오지 못했습니다')
      set_loading(false)
      return
    }
    const repMessage = JSON.parse(resp.repMessage)
    set_rows(JSON.parse(repMessage.records || '[]'))
    set_error('')
    set_loading(false)
  }, [targetUserId])

  useEffect(() => {
    set_loading(true)
    fetchRows()
  }, [fetchRows])

  /** 지갑 순서를 못박는다 -- 서버 정렬에 기대면 같은 화면이 새로고침마다 순서가 바뀐다. */
  const wallets = rows.slice().sort((a, b) => (a.id ?? 0) - (b.id ?? 0))

  const renderField = (row, f) => {
    const raw = f.unit === 'text' ? optStr(row[f.key]) : row[f.key]
    const num = f.unit === 'text' ? null : Number(row[f.key])
    const shown = f.unit === 'text'
      ? (raw ?? '전 눈금 1.0')
      : (Number.isFinite(num) ? String(row[f.key]) : '—')
    const percent = f.unit === 'ratio' ? asPercent(row[f.key]) : null
    const isZero = Number.isFinite(num) && num === 0

    return (
      <div key={f.key}>
        <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
          <Typography variant='body2'>
            <b>{f.label}</b> {shown}{f.unit === 'x' && Number.isFinite(num) ? 'x' : ''}
          </Typography>

          {/* 소수 옆의 % 는 **덧붙임이지 변환이 아니다.** 원문을 지우면 DB에 뭐가 적혀 있는지를
              화면에서 확인할 수 없게 되고, 이 화면의 용도가 그 확인이다. */}
          {percent && (
            <Typography variant='caption' color='text.secondary'>
              ({percent})
            </Typography>
          )}

          {/* 같은 0 인데 뜻이 갈린다 -- 그 차이를 화면이 말하지 않으면 사람이 0 을 다 같은
              것으로 읽는다. `reject` 쪽은 **그 지갑이 기동에서 빠진다**는 뜻이다. */}
          {isZero && f.zero === 'off' && (
            <Chip label='끔' size='small' variant='outlined' />
          )}
          {isZero && f.zero === 'reject' && (
            <Chip label='기동 거부' size='small' color='error' variant='outlined' />
          )}
        </Stack>

        <Typography variant='caption' color='text.secondary'>
          {f.desc}
        </Typography>
      </div>
    )
  }

  const renderWallet = (row) => {
    const note = optStr(row.note)

    return (
      <div key={row.id}>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
          <Typography variant='body2'><b>{row.name}</b></Typography>
          <Typography variant='caption' color='text.secondary'>
            walletId {row.id} · {row.runMode} · {row.status}
          </Typography>
        </Stack>

        {note && (
          <Typography variant='caption' color='text.secondary' display='block'>
            메모: {note}
          </Typography>
        )}

        <Stack spacing={1} sx={{ mt: 1 }}>
          {FIELDS.map((f) => renderField(row, f))}
        </Stack>
        <Divider sx={{ mt: 1, mb: 1 }} />
      </div>
    )
  }

  return (
    <>
      {userId && (
        <Stack direction='row' alignItems='center' spacing={1}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackOutlinedIcon />
          </IconButton>
          <Typography variant='body2'>userId {userId} 의 설정</Typography>
        </Stack>
      )}

      {error && (
        <Alert severity='error' sx={{ mb: 1 }}>{error}</Alert>
      )}

      {/* ⚠ **이 값들은 조립 시점에 고정된다.** 돌고 있는 프로세스에 대고 DB 를 고쳐도 다음
          재기동까지 아무 일도 안 일어난다 -- 그래서 이 화면에 저장 창구가 없다. 실제로 무엇으로
          돌고 있는지는 기동 로그의 `[SHELL]` 과 `지갑 리스크 배선` 줄이 찍는다. */}
      <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
        읽기 전용입니다. 이 값들은 조립 시점에 고정되므로 고쳐도 다음 재기동부터 먹습니다 --
        지금 무엇으로 돌고 있는지는 기동 로그의 [SHELL] 줄이 정본입니다.
      </Typography>

      {loading && <Typography variant='body2'>불러오는 중...</Typography>}
      {!loading && wallets.length === 0 && (
        <Typography variant='body2'>
          이 유저에게 등록된 지갑이 없습니다
        </Typography>
      )}

      {wallets.map(renderWallet)}
    </>
  )
}

export default UscPage
